import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Cell
} from 'recharts';

const COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#f97316', '#ec4899'];

function formatCurrency(value) {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value}`;
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="report-chart-tooltip">
      <p className="report-chart-tooltip-label">{label}</p>
      {payload.map((entry, i) => (
        <p key={i} style={{ color: entry.color }}>
          {entry.name}: {formatCurrency(entry.value)} ({entry.payload.count} deals)
        </p>
      ))}
    </div>
  );
};

export default function PipelineChart({ data, groupBy, onDataPointClick }) {
  if (!data || data.length === 0) {
    return <div className="report-chart-empty">No pipeline data for the selected period.</div>;
  }

  // Pivot data: group_label on X axis, stage_name as stacked bars
  const stages = [...new Set(data.map(d => d.stage_name).filter(Boolean))];
  const groups = [...new Set(data.map(d => d.group_label).filter(Boolean))];

  const chartData = groups.map(group => {
    const entry = { group_label: group };
    stages.forEach(stage => {
      const match = data.find(d => d.group_label === group && d.stage_name === stage);
      entry[stage] = match ? match.total_amount : 0;
      entry[`${stage}_count`] = match ? match.count : 0;
    });
    return entry;
  });

  const handleClick = (stageKey, entry) => {
    if (onDataPointClick) {
      onDataPointClick({
        groupLabel: entry.group_label,
        stageName: stageKey,
      });
    }
  };

  return (
    <ResponsiveContainer width="100%" height={400}>
      <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
        <XAxis
          dataKey="group_label"
          tick={{ fontSize: 12, fill: 'var(--color-text-secondary)' }}
          angle={-30}
          textAnchor="end"
          height={70}
        />
        <YAxis
          tickFormatter={formatCurrency}
          tick={{ fontSize: 12, fill: 'var(--color-text-secondary)' }}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend />
        {stages.map((stage, i) => (
          <Bar
            key={stage}
            dataKey={stage}
            stackId="pipeline"
            fill={COLORS[i % COLORS.length]}
            cursor="pointer"
            onClick={(entry) => handleClick(stage, entry)}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}
